import { useEffect, useState, type FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { LuChevronRight } from 'react-icons/lu';
import BrikAgente, { type CaraBrik, type EstadoBrik } from './BrikAgente';
import AssistenteMarkdown from './AssistenteMarkdown';
import type { AberturaAssistente } from '../services/AssistenteService';
import './BrikFala.css';

export type EstadoFala = 'carregando' | 'pronto' | 'erro';

/**
 * O Brik falando na Home: ele à esquerda, alinhado pelo pé, e a saudação num
 * balão que sai dele (opção B do desenho fechado,
 * `docs/designsystem/propostas/home-brik.html`, 07/10/2026).
 *
 * A saudação é daqui e aparece na hora; a frase vem da API (`fala`, montada por
 * regra fixa, sem o modelo) e entra no balão quando chega. Enquanto isso, o Brik
 * pensa. Se a API falhar, o balão fica só com a saudação e um aviso de uma linha:
 * a Home continua servindo para perguntar.
 */
const BrikFala: FC<{
    saudacao: string;
    estado: EstadoFala;
    abertura?: AberturaAssistente | null;
    /** Conta nova: o próximo dos Primeiros passos que falta, para o balão apontar. */
    proximoPasso?: string | null;
    /** Celular: o Brik desce para 64px. */
    celular?: boolean;
}> = ({ saudacao, estado, abertura, proximoPasso, celular = false }) => {
    const navigate = useNavigate();

    /* Quando a fala chega, ele fala: a boca abre e fecha por 1,6 s (direção D da
       boca, 07/10/2026). No comemorando é a festa, que o BrikAgente faz sozinho. */
    const [chegou, setChegou] = useState(false);
    useEffect(() => {
        if (estado !== 'pronto') return;
        setChegou(true);
        const t = setTimeout(() => setChegou(false), 1600);
        return () => clearTimeout(t);
    }, [estado]);

    const contaNova = estado === 'pronto' && !!abertura?.contaNova;

    let cara: CaraBrik = 'atento';
    let estadoBrik: EstadoBrik = 'repouso';
    if (estado === 'carregando') {
        estadoBrik = 'pensando';
    } else if (estado === 'pronto' && abertura) {
        /* O humor "feliz" da API é o Brik de olho aberto e vivo: na Home ele não
           fecha o olho (pedido dele em 07/10, "aberto e piscando, movendo os
           olhos"). Só o dormindo, do dia em que a empresa não abre, fecha. */
        cara = contaNova || abertura.humor === 'feliz' ? 'atento' : abertura.humor;
        if (chegou && cara === 'atento') estadoBrik = 'falando';
    }

    return (
        <div className="hc-fala">
            {/* Na tela inicial ele acompanha o mouse com os olhos (07/10/2026). */}
            <BrikAgente tamanho="grande" px={celular ? 64 : 96} cara={cara} estado={estadoBrik} vivo segueMouse />

            <div className="hc-balao" aria-busy={estado === 'carregando'}>
                <h1 className="hc-tt">{saudacao}</h1>

                <div className="hc-tx" aria-live="polite">
                    {estado === 'carregando' && (
                        <>
                            <span className="hc-mudo">Olhando o seu dia…</span>
                            {/* Opção B (07/10/2026): o balão já nasce com a altura da fala
                                de sempre — três linhas, quatro no celular — e nada se mexe
                                quando ela chega. */}
                            <span className="hc-linhas" aria-hidden="true">
                                <i />
                                <i />
                                {celular && <i />}
                            </span>
                        </>
                    )}

                    {estado === 'erro' && <span>Pode perguntar normalmente: hoje não consegui olhar o seu dia.</span>}

                    {contaNova && (
                        <span>
                            Sua conta está começando, então ainda não tenho o que olhar.
                            {proximoPasso && <> O próximo dos <b>Primeiros passos</b> é <b>{proximoPasso}</b>.</>}
                        </span>
                    )}

                    {estado === 'pronto' && abertura && !contaNova && <AssistenteMarkdown texto={abertura.fala} />}
                </div>

                {contaNova && proximoPasso && (
                    <button type="button" className="hc-link" onClick={() => navigate('/primeiros-passos')}>
                        Continuar os Primeiros passos <LuChevronRight size={14} />
                    </button>
                )}
            </div>
        </div>
    );
};

export default BrikFala;
